'use client';
import React, { useEffect, useMemo, useState } from 'react';
import { useGed } from '@/lib/store';
import { EQUIP_STATES, EQUIP_CATEGORIES, exigeMetrologie, natureEquip, porteResultat, vocabMeta, fmtDate, MOTIF_DEROGATION_MIN,
  CATEGORIE_LOGICIEL, jourIso, aujourdhuiIso } from '@/lib/meta';
import { rubriquesManquantes } from '@/lib/rubriques-equipement';
import { useRemonteEnHaut, I, Modale, card, inputStyle, FilterBar, EmptyState, FieldError, RequiredHint, useBusy, RefISO, Selecteur, Skeleton, Pastille, enChargement, SelecteurPastilles } from '@/components/ui';
import { useTri, BoutonsTri } from '@/shared/Tri';
import { OngletsDossier, PanneauDossier } from '@/components/Dossier';
import { BoutonJournal } from '@/components/BoutonJournal';
import { Liens } from '@/components/Liens';
// Le panneau « dire ce que le geste emporte », partagé avec les registres qualité.
import { SuppressionRaisonnee } from '@/components/SuppressionRaisonnee';
import { lbl, pill, H1 } from './commun';
import { Identification, FicheDeVie, Pieces, Etiquette, NCLiees } from './EquipFiche';
import { ExamensEquipement } from './ExamensEquipement';
import { ncsDeLEquipement } from '@/lib/nc-equipement';
import { PanneauAccessoires } from './EquipAccessoires';
import { ChoixHabilitations, useHabilitationsProposees } from '@/components/screens/PostesEquipements';
import { Calibrations } from './Calibrations';
import { ChoixCorrespondant } from '@/components/ChoixCorrespondant';
import { FAMILLES_SOCIETE } from './ContactsEquipement';
import { Maintenances } from './Maintenances';
import { MiseEnService } from './MiseEnService';
import { etatEquip, avancement } from '@/shared/miseEnService';
import type { GedEquipement, ImpactSortieEquip } from '@/lib/types';
import { Norme } from '../../Norme';

import { Bouton } from '@/shared/Bouton';
import { peutGererParc } from '@/lib/droits-parc';
// Équipements & métrologie (G3) — parc, fiche signalétique, fiche de vie,
// pièces jointes, échéances.
//
// Cet écran ne porte plus que la LISTE et la sélection : le dossier d'un
// équipement vit dans `EquipFiche.tsx`. La fiche est passée de sept champs à
// l'intégralité du gabarit signalétique du laboratoire, et un fichier unique
// aurait de nouveau dépassé le millier de lignes — ce qu'on venait justement
// de défaire sur les écrans « ressources ».
/* ------------------------------ Équipements ------------------------------ */
/**
 * Sortie du service d'un appareil — ISO 15189 §6.4.4.
 *
 * Le geste se faisait d'un clic, sans un mot : le journal portait « Statut :
 * en_service → hors_service » et rien d'autre (recette A15). Un an plus tard,
 * personne ne pouvait dire si l'appareil était réparable, où il était passé, ni
 * si sa dernière semaine de résultats méritait un regard.
 *
 * Ce n'est PAS une annulation — l'appareil a existé et a produit des résultats
 * qui restent opposables. C'est une sortie, datée et motivée.
 */
function SortieDuService({ e, onFermer }: { e: GedEquipement; onFermer: () => void }) {
  const { smq, cartesCqi, impactSortieEquip } = useGed();
  const [motif, setMotif] = useState('');
  const cartes = (cartesCqi || []).filter((c) => c.equipement === e.code);

  // Aperçu SEUL (aucune écriture) de ce que la sortie du service emporte sur
  // les techniques et les examens — demandé par la RQ le 13/09/2026 : elle
  // veut voir cette liste AVANT de confirmer, pas la découvrir après coup au
  // manuel. Chargé à l'ouverture du formulaire, comme les cartes CQI ci-dessus
  // qui viennent déjà du store sans geste supplémentaire.
  const [impact, setImpact] = useState<ImpactSortieEquip | null>(null);
  useEffect(() => {
    let vivant = true;
    impactSortieEquip(e.documentId).then((r) => { if (vivant) setImpact(r); });
    return () => { vivant = false; };
  }, [e.documentId, impactSortieEquip]);

  return (
    <div style={{ margin: '10px 0' }}>
      <SuppressionRaisonnee
        titre="Sortir cet équipement du service"
        objet={<><strong>{e.code}</strong> — {e.nom}</>}
        nature="retrait"
        consequences={[
          ...(cartes.length ? [{
            cle: 'cqi',
            texte: `${cartes.length} carte(s) de contrôle interne le suivent encore.`,
            gravite: 'attention' as const,
            detail: 'Elles cessent d’être alimentées : rattachez-les à l’appareil qui reprend l’examen, sinon la surveillance s’arrête sans le dire.',
          }] : []),
          // Une conséquence D'AVERTISSEMENT — jamais `bloquant`, qui retirerait
          // le bouton de confirmation : la RQ veut être informée puis décider,
          // pas empêchée d'agir.
          ...(impact?.techniquesADesactiver.length ? [{
            cle: 'techniques',
            texte: `${impact.techniquesADesactiver.length} technique(s) vont être désactivée(s) : ${impact.techniquesADesactiver.map((t) => t.code).join(', ')}.`,
            gravite: 'attention' as const,
            detail: 'Cet appareil était leur seul équipement encore en service — la case « active » de la technique sera décochée dès la confirmation.',
          }] : []),
          // Séparée et plus grave que la précédente : c'est exactement le cas
          // que la RQ a nommé « surtout » — un examen qui ne pourra plus être
          // réalisé du tout, faute de la moindre technique active.
          ...(impact?.examensSansTechnique.length ? [{
            cle: 'examens-sans-technique',
            texte: `${impact.examensSansTechnique.length} examen(s) vont se retrouver SANS AUCUNE technique active.`,
            gravite: 'attention' as const,
            detail: impact.examensSansTechnique.map((f) => `${f.code} — ${f.libelle}`).join(' · '),
          }] : []),
          {
            cle: 'echeances',
            texte: 'L’appareil sort des échéances de métrologie et du plan de maintenance.',
            gravite: 'attention' as const,
          },
          {
            cle: 'trace',
            texte: 'La fiche, sa fiche de vie et ses résultats passés restent intacts.',
            gravite: 'info' as const,
            detail: 'La date de sortie et le motif ci-dessous rejoignent la fiche et le journal signé.',
          },
        ]}
        note={(
          <div style={{ display: 'grid', gap: 6 }}>
            <label style={lbl} htmlFor={`sortie-${e.documentId}`}>
              Motif de la sortie <span style={{ color: 'var(--red-600)' }}>*</span>
            </label>
            <textarea id={`sortie-${e.documentId}`} value={motif} rows={2}
              onChange={(ev) => setMotif(ev.target.value)}
              placeholder="Ex. réformé après panne de la carte mère, remplacé par EQ42 le 12/07."
              style={{ ...inputStyle, height: 'auto', padding: '9px 12px', resize: 'vertical' }} />
            <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
              {motif.trim().length}/{MOTIF_DEROGATION_MIN} caractères minimum — relu en audit.
            </span>
          </div>
        )}
        libelleAction="Sortir du service"
        onConfirmer={async () => {
          if (motif.trim().length < MOTIF_DEROGATION_MIN) {
            return `Motif trop court : ${MOTIF_DEROGATION_MIN} caractères au minimum.`;
          }
          const ok = await smq(`ged/equip/${e.documentId}/status`,
            { statut: 'hors_service', motif: motif.trim() },
            'Équipement sorti du service — motif inscrit à la fiche de vie.',
            `Sortie du service de ${e.code || e.nom}`);
          if (ok) onFermer();
          return ok ? '' : 'La sortie du service n’a pas abouti.';
        }}
        onAnnuler={onFermer} />
    </div>
  );
}

const TRI_PARC_DEFAUT = { cle: 'echeance', sens: 1 as const };

export function Equipements() {
  const { equips, session, smq, smqData, settings, selectedEquip, select, maintenances, loading, booted, inclureRecette, setInclureRecette } = useGed();
  /* BLOQ-03 : « Aucun équipement au parc » est une affirmation sur le parc, pas
     sur un filtre — la servir avant l'arrivée des données ferait croire à un
     inventaire vide sur un écran qui en porte 253. */
  const attend = enChargement(booted, loading, equips.length);
  const peutParc = peutGererParc(session.roles);
  const [showNew, setShowNew] = useState(false);
  /* La fiche qui vient d'être créée : elle s'ouvre sur « Mise en service ». */
  const [accueil, setAccueil] = useState<string | null>(null);
  const [panneau, setPanneau] = useState(false);
  /* ⚠️ `emplacementCode` ET NON `localisation` (relevé DAT-1…4, 27/08/2026).
     Le champ était libre : 54 graphies exactes s'y étaient accumulées pour 19
     pièces — `BACTERIO`, `bacterio`, `BACTERIOLOGIE`, `Bactériologie`,
     `Salle bacterio`, `Salle microbiologie` désignent la même salle. Chaque
     création en ajoutait une.
     ⚠️ La fiche d'un équipement garde, elle, un `localisation` LIBRE : c'est la
     transcription de la fiche papier, et c'est voulu. Ce qui change ici, c'est
     que la SALLE ne se tape plus — elle se choisit. */
  /* ⚠️ TROIS RUBRIQUES AJOUTÉES LE 12/09/2026 — modèle, fournisseur, date de
     mise en service. Le formulaire n'en demandait que trois des six exigées par
     le §6.4.2 : toute fiche naissait donc incomplète, quoi qu'on eût saisi, et
     le texte l'assumait (« complétez-la ensuite »). C'est ce qui a produit
     **224 fiches incomplètes sur 248** — un formulaire qui ne demande pas ce
     qu'il exige fabrique exactement la dette qu'il signale ensuite. */
  /* ⚠️ `nature` ET LES RUBRIQUES DU LOGICIEL SONT DEMANDÉES ICI (05/10/2026),
     pour exactement la raison écrite ci-dessus. La famille « Logiciel » POSE la
     nature `logiciel` — les deux sont couplées, et le moteur refuse la
     contradiction. Et puisque la liste exigée §6.4.2 d'un logiciel comporte sa
     VERSION et l'endroit où il tourne, ne pas les demander à la création
     reproduirait la dette qu'on vient de solder : ce qu'un formulaire ne
     demande pas, personne ne le complète ensuite.
     `logicielVersion` / `logicielReleveLe` sont à plat dans l'état du
     formulaire et recomposés en bloc `logiciel` à l'envoi : un état imbriqué
     dans un formulaire de dix champs se met à jour par recopie, et c'est ainsi
     qu'on perd une clé sans s'en apercevoir. */
  const NE_VIDE = {
    nom: '', categorie: 'automate', nature: 'mesure', marque: '', modele: '', numeroSerie: '',
    emplacementCode: '', discipline: '', fournisseurCode: '', dateMiseEnService: '',
    periodiciteMois: 12,
    logicielVersion: '', logicielReleveLe: '',
    ordinateursCodes: [] as string[], accesEnLigne: false,
    equipementsPilotes: [] as string[],
  };
  const [ne, setNe] = useState(NE_VIDE);
  const { habs: habsRef, proposees: habProposees } = useHabilitationsProposees(ne.discipline, ne.nature);
  /* La famille choisie commande l'allure du formulaire : sept rubriques
     disparaissent, trois apparaissent. Dérivé, jamais stocké en double. */
  const estLogicielNeuf = ne.categorie === CATEGORIE_LOGICIEL;
  /** Les ordinateurs proposables. Les postes et serveurs (`PC*`) EN TÊTE : les
   *  154 fiches informatiques du parc mêlent les 33 ordinateurs aux claviers,
   *  souris, imprimantes et onduleurs. C'est une aide à la saisie, pas une
   *  règle — le moteur, lui, contrôle la FAMILLE, vérifiable à l'œil dans un
   *  export, et non le préfixe du code, qui se trompe dans les deux sens. */
  const ordinateursDuParc = useMemo(() => (equips || [])
    .filter((x) => x.code && x.categorie === 'informatique' && x.actif !== false)
    .sort((a, b) => {
      const pa = /^PC/i.test(String(a.code)) ? 0 : 1;
      const pb = /^PC/i.test(String(b.code)) ? 0 : 1;
      return pa - pb || String(a.code).localeCompare(String(b.code), 'fr', { numeric: true });
    })
    .map((x) => ({ documentId: String(x.code), code: String(x.code), nom: x.nom })), [equips]);
  /** Ce qu'un logiciel peut piloter : tout appareil du parc sauf un logiciel. */
  const pilotablesDuParc = useMemo(() => (equips || [])
    .filter((x) => x.code && x.nature !== 'logiciel' && x.actif !== false)
    .sort((a, b) => String(a.code).localeCompare(String(b.code), 'fr', { numeric: true }))
    .map((x) => ({ documentId: String(x.code), code: String(x.code), nom: x.nom })), [equips]);
  /** Les pièces du laboratoire, servies par le référentiel des endroits. */
  const [pieces, setPieces] = useState<{ code: string; libelle: string }[]>([]);
  React.useEffect(() => {
    let vivant = true;
    void fetch('/api/ged/ged/emplacements', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => {
        if (!vivant || !j) return;
        setPieces(((j.emplacements || []) as { code: string; libelle: string; niveau?: string; actif?: boolean }[])
          .filter((e) => e.niveau === 'piece' && e.actif !== false)
          .map((e) => ({ code: e.code, libelle: e.libelle })));
      })
      .catch(() => {});
    return () => { vivant = false; };
  }, []);
  const [erreurEquip, setErreurEquip] = useState('');
  /* Les postes retenus à l'ajout (lot N7-4). Vide = proposition refusée, et un
     refus ne laisse aucune trace : rien n'est envoyé, rien n'est écrit. */
  // Les habilitations de l'appareil, choisies à l'ajout (28/10/2026) — elles décident de ses postes.
  // `null` tant qu'on n'y a pas touché : la proposition fait alors foi.
  const [habManuelles, setHabChoisies] = useState<string[] | null>(null);
  const habChoisies = habManuelles ?? habProposees;
  const { run: runEquip, estOccupe: occupeEquip } = useBusy();
  const sel = equips.find((e) => e.documentId === selectedEquip) || null;
  // Ouvrir ou refermer un dossier REMPLACE tout l'écran : même règle que les
  // sous-onglets (24/08/2026) — c'est une arrivée, on remonte en haut.
  useRemonteEnHaut(selectedEquip);
  const discs = settings?.disciplines?.filter((d) => d.active) || [];
  /* ── L'ÉCHÉANCE DE MÉTROLOGIE — lot EQP-1 ────────────────────────────────
   *
   * Deux défauts corrigés d'un coup ici.
   *
   * 1. La SOURCE. `e.prochaineEcheance` était une colonne écrite à chaque
   *    intervention : fausse dès qu'une maintenance était faite en avance, en
   *    retard, ou la périodicité changée — sans que rien ne le signale. La date
   *    vient désormais du parc servi par le registre de maintenance, où elle est
   *    DÉRIVÉE de la dernière intervention consignée et de la périodicité
   *    courante. Une seule source, un seul calcul, pour cet écran, le tableau de
   *    bord et le registre.
   *
   * 2. La COMPARAISON. `new Date(d) < new Date()` compare des INSTANTS : une
   *    échéance due AUJOURD'HUI passait « dépassée » à 00 h 01. Le projet a déjà
   *    payé cette erreur (cf. `maintenance-echeances.ts`). On compare des JOURS,
   *    sur des chaînes `AAAA-MM-JJ`, qui s'ordonnent lexicographiquement. */
  const auj = aujourdhuiIso();
  const echeanceDe = useMemo(() => {
    const m = new Map<string, { date: string | null; etat: string }>();
    for (const p of maintenances?.parc || []) {
      if (p.code) m.set(p.code, { date: p.echeance?.date || p.echeancePosee, etat: p.echeance?.etat || 'indeterminee' });
    }
    return m;
  }, [maintenances]);
  /** Date d'échéance à AFFICHER pour un appareil, ou `null` si indéterminée. */
  const echeance = (e: { code?: string }) => (e.code ? echeanceDe.get(e.code)?.date || null : null);
  /** ⚠️ `false` ne veut pas dire « à jour » : une échéance indéterminée n'est
   *  pas dépassée, et n'est pas conforme non plus. Le bandeau de couverture de
   *  l'écran des maintenances est le seul endroit qui compte les deux. */
  const overdue = (d: string | null | undefined) => !!d && jourIso(d) < auj;

  // Le parc s'affichait brut, dans l'ordre du serveur : « quels équipements ont
  // une métrologie échue ? » se répondait en faisant défiler toute la liste.
  const [q, setQ] = useState('');
  const [filtre, setFiltre] = useState('tous');
  // ── Famille, filtre ORTHOGONAL à l'état ────────────────────────────────────
  // La reprise du 06/08/2026 a fait passer le parc de 28 à 237 fiches, dont 154
  // équipements informatiques (postes, écrans, claviers, onduleurs). Sans ce
  // filtre, « quel automate a une échéance dépassée ? » se répond en faisant
  // défiler 150 souris — et le parc analytique, celui qui rend des résultats,
  // devient invisible dans sa propre liste.
  //
  // Il est SÉPARÉ du filtre d'état (échéance, fiche incomplète, raccordement)
  // et non fondu dedans : les deux questions se posent ensemble — « quelle
  // pipette n'est pas raccordée » — et un filtre unique obligerait à choisir.
  //
  // Le parc s'ouvre sur les AUTOMATES (retour Ines du 17/08). Sur 237 fiches,
  // 154 sont informatiques : ouvrir sur « toutes » revenait à présenter d'abord
  // des souris et des écrans à qui vient voir son plateau technique. Les autres
  // familles restent à un clic, et le compteur de chaque onglet dit ce qu'on ne
  // regarde pas — un filtre par défaut qui CACHE sans le dire est pire que pas
  // de filtre.
  const [famille, setFamille] = useState('analytique');
  // ── Tri de la liste du parc ────────────────────────────────────────────────
  // Le code d'inventaire et la désignation sont deux données distinctes : on
  // cherche tantôt « EQ03 », tantôt « Erba Lyte Pro », et l'ordre par échéance
  // ne répond ni à l'une ni à l'autre. La liste étant une colonne étroite (elle
  // porte aussi les pastilles d'état), les trois entrées de tri sont posées en
  // tête de la carte plutôt qu'en en-tête de colonnes — mais ce sont bien les
  // mêmes boutons triables que les tableaux du module.
  /* L4 (10/10/2026) : le MÊME moteur de tri que les tableaux (`useTri`), plus
     de `SortableTh`. L'échéance reste le tri par défaut — c'est la question que
     l'écran pose en premier. Sa clé range les ÉCHUES en tête, puis la plus
     proche, et sans échéance dérivable en fin de liste (229 fiches
     indéterminées repousseraient sinon les échéances réelles hors de l'écran). */
  const valeursTri = useMemo<Record<string, (e: GedEquipement) => string>>(() => ({
    code: (e) => String(e.code || ''),
    nom: (e) => e.nom,
    echeance: (e) => { const ec = echeance(e); return `${overdue(ec) ? '0' : '1'}|${ec || '9999-99-99'}`; },
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }), [echeanceDe]);
  const { tri, basculer: onTri, trier } = useTri<GedEquipement>(TRI_PARC_DEFAUT, valeursTri);
  /* ⚠️ « LOGICIELS » EST UNE FAMILLE À PART (05/10/2026) — c'est la demande
     même : « je voudrais avoir une catégorie pour les logiciels ». Sans elle,
     Boxylab et le SCA se cherchaient parmi les 154 fiches informatiques, entre
     les claviers et les souris.
     ⚠️ ELLE SORT AUSSI D'« AUTRES » : la branche de repli était « ni automate,
     ni informatique, ni intermédiaire », si bien qu'une famille ajoutée y
     tombait toute seule, en silence. Les logiciels s'y seraient rangés avec le
     mobilier, et le compteur d'« Autres » aurait grossi sans que personne
     comprenne pourquoi. */
  const dansFamille = (e: { categorie: string }) => estDeLaFamille(e.categorie, famille);
  /* ── LES DEUX BARRES DE FILTRES SE CONTREDISAIENT (relevé MAJ-14) ─────────
     Mesuré le 26/08/2026 : le segment « Automates (36) » était actif en haut,
     le compteur annonçait « 36 sur 36 », et le chip « Tous (253) » juste en
     dessous était marqué actif lui aussi. Deux états sélectionnés, deux
     totaux inconciliables.

     Les deux barres ne se contredisent pas vraiment — ce sont deux AXES : la
     famille d'appareil, puis l'état dans cette famille. Mais les compteurs du
     bas se calculaient sur le parc ENTIER, si bien qu'ils décrivaient un autre
     ensemble que celui qu'on regardait. Un compteur qui compte autre chose que
     ce qu'on voit est pire qu'un compteur absent.

     Ils se calculent désormais DANS la famille choisie : « Tous » y affiche 36
     quand « Automates » est actif, et la seconde barre se lit enfin comme ce
     qu'elle est — un filtre à l'intérieur du premier. */
  const duParc = equips.filter(dansFamille);
  const echus = duParc.filter((e) => e.statut !== 'hors_service' && overdue(echeance(e)));
  const enService = duParc.filter((e) => e.statut === 'en_service');
  const arretes = duParc.filter((e) => e.statut !== 'en_service');
  // Une fiche d'identification incomplète est un ÉCART (15189 §6.4.2) : elle se
  // filtre comme les échéances dépassées, et non au détour d'une fiche ouverte
  // par hasard. Sept équipements du parc sont dans ce cas, dont deux en
  // production.
  const incompletes = duParc.filter((e) => e.ficheComplete === false);
  // Un appareil de prêt encore présent est un appareil à RENDRE : sans ce
  // filtre, on ne s'en aperçoit que le jour où le fournisseur le réclame.
  const prets = duParc.filter((e) => e.estPret && !e.pret?.dateRestitution);
  // Équipements intermédiaires (registre J2) dont le raccordement au SI n'est pas
  // établi alors qu'ils servent : c'est le trou que l'annexe du 28/07 signalait,
  // et il ne se voit qu'à condition de pouvoir le filtrer.
  const nonRaccordes = duParc.filter((e) => exigeMetrologie(e.categorie)
    && e.statut === 'en_service'
    && (!e.metrologie?.raccordement || e.metrologie.raccordement === 'aucun'));
  // Fiches dont le CODE a été fabriqué par l'outil faute de code au classeur, ou
  // dont la désignation reste à préciser. C'est une DETTE DE SAISIE, et elle ne
  // se voit qu'à condition de pouvoir la filtrer : mentionnée seulement sur la
  // fiche, elle se perdrait dans 245 lignes et personne n'irait jamais lire
  // l'étiquette de l'appareil.
  const aRegulariser = duParc.filter((e) => e.codeProvisoire === true);
  // Les équipements de MÉTROLOGIE : ceux qui ne rendent aucun résultat mais
  // dont la température, la vitesse, la durée ou le flux se raccordent. Sans ce
  // filtre ils ne se cherchent que par famille — or la famille est « enceinte »
  // ou « petit matériel », et c'est la NATURE qu'on vient vérifier quand on se
  // demande de quoi le raccordement est dû.
  const deMetrologie = duParc.filter((e) => e.nature === 'metrologie');
  // « Non renseigné » n'est pas « non » : les deux ne se comptent jamais ensemble.
  const silSansReponse = duParc.filter((e) => !e.sil?.relie);
  const silNonRelies = duParc.filter((e) => e.sil?.relie === 'non');
  const equipsFiltres = useMemo(() => {
    const base = filtre === 'echus' ? echus
      : filtre === 'incompletes' ? incompletes
        : filtre === 'prets' ? prets
          : filtre === 'nonraccordes' ? nonRaccordes
            : filtre === 'metrologie' ? deMetrologie
              : filtre === 'regulariser' ? aRegulariser
                : filtre === 'service' ? enService
                  : filtre === 'arret' ? arretes
                  : filtre === 'silsansreponse' ? silSansReponse
                    : filtre === 'silnon' ? silNonRelies : equips;
    const parFamille = base.filter(dansFamille);
    const qq = q.trim().toLowerCase();
    const cherche = qq
      ? parFamille.filter((e) => `${e.code || ''} ${e.nom} ${e.marque} ${e.modele || ''} ${e.numeroSerie} ${e.localisation} ${e.discipline}`.toLowerCase().includes(qq))
      : parFamille;
    return trier(cherche);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [equips, filtre, famille, q, trier]);

  return (
    <div style={{ display: 'grid', gap: 18 }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
        {/* ⚠️ « Équipements », et non « Équipements & métrologie » (relevé
            SYS-16 du 26/08/2026). Un écran `/metrologie` DISTINCT existe, avec
            son propre registre de vérifications : un titre qui annonce les deux
            laisse croire qu'on est au bon endroit pour la métrologie, et l'on
            cherche longtemps ce qui n'y est pas. Le titre dit maintenant la même
            chose que l'entrée de menu — c'est par elle qu'on est arrivé. */}
        <H1 eyebrow={<>Ressources · <RefISO code="6.4" /><Norme art="6.4" /></>} title="Équipements" />
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {/* Le panneau PROPOSE : il ne bascule rien de lui-même. Il est hors de
              la liste parce qu'il porte sur le parc entier, pas sur la fiche
              sélectionnée — et parce qu'on n'y vient qu'une fois, pour trancher. */}
          <Bouton taille="sm" onClick={() => setPanneau(!panneau)} aria-pressed={panneau}>
            <I n="eye" s={14} />Accessoires & qualification
          </Bouton>
          {peutParc && <Bouton variante="primaire" onClick={() => setShowNew(!showNew)}><I n="plus" s={15} />Nouvel équipement</Bouton>}
        </div>
      </div>
      {panneau && <PanneauAccessoires />}
      {/* ⚠️ UNE FENÊTRE (12/09/2026). « Quand je crée un nouvel équipement, je
          voudrais un popup » — RQ, 11/09. Ce panneau s'ouvrait plus près de son
          bouton que les trois autres du même lot, mais il souffrait du même
          défaut de fond : il poussait la liste du parc vers le bas, et la saisie
          se faisait au milieu d'un écran qui avait bougé sous les yeux. La
          fenêtre apporte en plus le focus piégé et l'alerte de sortie — sur un
          formulaire qu'on ouvre en pleine consultation du parc, c'est justement
          ce qui manquait.
          ⚠️ CE LOT NE TRAITE QUE LA FENÊTRE. Le second volet du signalement 022
          — couvrir tous les onglets et SIGNALER les rubriques exigées par la
          15189 — est au lot 6, avec 019 : c'est la même liste
          (`RUBRIQUES_EXIGEES`), et trois de ses six rubriques manquent encore
          ici (modèle, fournisseur, date de mise en service). C'est ce qui
          produit les 224 fiches incomplètes sur 248. */}
      {showNew && (
        <Modale titre="Nouvel équipement" largeur={680}
          onClose={() => {
                setShowNew(false); setHabChoisies(null); setErreurEquip('');
                setNe(NE_VIDE);
          }}
          pied={<>
            <Bouton taille="sm" onClick={() => {
                setShowNew(false); setHabChoisies(null); setErreurEquip('');
                setNe(NE_VIDE);
              }} disabled={occupeEquip()}>
              Annuler
            </Bouton>
            <Bouton variante="primaire" taille="sm" onClick={() => void runEquip('creer', async () => {
              if (!ne.nom.trim()) { setErreurEquip('Le nom de l’équipement est obligatoire.'); return; }
              setErreurEquip('');
              // Les postes retenus voyagent AVEC la création : un second appel
              // que l'écran pourrait ne jamais faire, c'est exactement la reprise
              // ultérieure qui n'a jamais lieu. Une liste VIDE est légitime —
              // c'est le refus de la proposition, et il ne laisse aucune trace.
              /* ⚠️ LE BLOC `logiciel` EST RECOMPOSÉ ICI, à partir des deux champs
                 plats du formulaire — même forme qu'à la correction de fiche, et
                 même refus côté moteur si la version n'est pas datée. */
              const { logicielVersion, logicielReleveLe, ordinateursCodes, accesEnLigne, equipementsPilotes, ...reste } = ne;
              /* ⚠️ LA FICHE NEUVE S'OUVRE SUR « MISE EN SERVICE » (28/10/2026). La
                 fenêtre ne demande que l'identité ; le reste — achat, personnel,
                 réactifs, examens — se relie ensuite, et c'est le parcours
                 d'accueil de cet onglet qui le montre. Sans cette ouverture, rien
                 ne menait de l'un à l'autre et la fiche restait telle que créée. */
              const r = await smqData<{ equipement?: { documentId?: string } }>('ged/equip', {
                ...reste,
                ...(estLogicielNeuf ? { ordinateursCodes, accesEnLigne, equipementsPilotes } : {}),
                logiciel: { version: logicielVersion, releveLe: logicielReleveLe },
                habilitations: habChoisies,
              },
                habChoisies.length
                  ? `Équipement ajouté au parc, couvert par ${habChoisies.length} habilitation(s). Suivez le parcours d’accueil.`
                  : 'Équipement ajouté au parc. Suivez le parcours d’accueil.');
              if (r) {
                setShowNew(false); setHabChoisies(null); setErreurEquip('');
                setNe(NE_VIDE);
                const neuf = r.equipement?.documentId;
                if (neuf) { setAccueil(neuf); select('selectedEquip', neuf); }
              }
            })} disabled={occupeEquip()}>
              {occupeEquip('creer') ? 'Enregistrement…' : 'Ajouter au parc'}
            </Bouton>
          </>}>
          <div style={{ display: 'grid', gap: 10 }}>
          <RequiredHint />
          <div className="ged-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 16px' }}>
            <div>
              <label style={lbl} htmlFor="eq-nom">Nom <span style={{ color: 'var(--red-600)' }}>*</span></label>
              <input id="eq-nom" value={ne.nom} onChange={(e) => { setNe({ ...ne, nom: e.target.value }); if (erreurEquip) setErreurEquip(''); }}
                aria-invalid={!!erreurEquip} style={{ ...inputStyle, borderColor: erreurEquip ? 'var(--red-600)' : undefined }} />
              <FieldError>{erreurEquip}</FieldError>
            </div>
            {/* ⚠️ LA FAMILLE POSE LA NATURE (05/10/2026). « Logiciel » et la
                nature `logiciel` sont couplées : le moteur refuse l'une sans
                l'autre, parce que deux axes libres de diverger auraient laissé
                une fiche de famille Logiciel porter des cartes de contrôle
                interne. On les écrit donc ensemble, ici, dans le même geste —
                et le formulaire change d'allure sous les yeux, ce qui dit mieux
                que n'importe quel texte que la famille n'est pas un libellé. */}
            <div><label style={lbl} htmlFor="eq-cat">Catégorie</label><select id="eq-cat" value={ne.categorie}
              onChange={(e) => setNe({
                ...ne,
                categorie: e.target.value,
                nature: e.target.value === CATEGORIE_LOGICIEL ? 'logiciel'
                  : (ne.nature === 'logiciel' ? 'mesure' : ne.nature),
                /* La salle choisie avant de basculer en « Logiciel » est VIDÉE :
                   le champ est retiré de l'écran, et une pièce qu'on n'y voit
                   plus mais qui s'enregistre quand même est une donnée que
                   personne n'a confirmée. */
                emplacementCode: e.target.value === CATEGORIE_LOGICIEL ? '' : ne.emplacementCode,
              })} style={inputStyle}>{EQUIP_CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}</select></div>
            <div><label style={lbl}>{estLogicielNeuf ? <>Éditeur <MarqueIso /></> : <>Marque <MarqueIso /></>}</label><input value={ne.marque} onChange={(e) => setNe({ ...ne, marque: e.target.value })} style={inputStyle} /></div>
            {/* Le MODÈLE n'est pas exigé d'un logiciel — il n'en a pas. Sa
                déclinaison commerciale, quand l'éditeur en distingue une (« SCA
                Human » face à « SCA Vet »), reste proposée sans marque 15189. */}
            <div><label style={lbl}>{estLogicielNeuf ? 'Module / déclinaison' : <>Modèle <MarqueIso /></>}</label><input value={ne.modele} onChange={(e) => setNe({ ...ne, modele: e.target.value })} style={inputStyle} /></div>
            <div><label style={lbl}>{estLogicielNeuf ? <>N° de licence / clé <MarqueIso /></> : <>N° série <MarqueIso /></>}</label><input value={ne.numeroSerie} onChange={(e) => setNe({ ...ne, numeroSerie: e.target.value })} style={{ ...inputStyle, fontFamily: 'var(--font-mono)' }} /></div>
            {/* LA VERSION — rubrique exigée d'un logiciel, et la demande même :
                « un logiciel a une version qu'il faut tracer ». Elle n'est
                demandée QUE pour un logiciel : sur un automate, la version
                embarquée se relève à la fiche, pas à la création. */}
            {estLogicielNeuf && (
              <div><label style={lbl} htmlFor="eq-ver">Version installée <MarqueIso /></label>
                <input id="eq-ver" value={ne.logicielVersion} placeholder="6.7.39"
                  onChange={(e) => setNe({ ...ne, logicielVersion: e.target.value })}
                  style={{ ...inputStyle, fontFamily: 'var(--font-mono)' }} /></div>
            )}
            {/* ⚠️ LA DATE DU RELEVÉ EST EXIGÉE DÈS QU'UNE VERSION EST SAISIE, et
                le moteur la refuse sans. Une version sans date est une
                affirmation sans date : au bout d'un an, personne ne sait si elle
                décrit le logiciel d'aujourd'hui ou celui de la reprise. */}
            {estLogicielNeuf && (
              <div><label style={lbl} htmlFor="eq-verd">Version relevée le{ne.logicielVersion.trim() ? <span style={{ color: 'var(--red-600)' }}> *</span> : null}</label>
                <input id="eq-verd" type="date" value={ne.logicielReleveLe}
                  onChange={(e) => setNe({ ...ne, logicielReleveLe: e.target.value })} style={inputStyle} /></div>
            )}
            {/* Choisi dans l'annuaire (28/10/2026) : le moteur en écrit le nom. */}
            <div><label style={lbl} htmlFor="eq-fournisseur">Fournisseur <MarqueIso /></label>
              <ChoixCorrespondant id="eq-fournisseur" valeur={ne.fournisseurCode} onChange={(v) => setNe({ ...ne, fournisseurCode: v })}
                familles={FAMILLES_SOCIETE} vide="— aucun fournisseur"
                aide="Les fournisseurs de l’annuaire. Absent ? Créez-le d’abord dans l’annuaire." /></div>
            <div><label style={lbl}>Mise en service <MarqueIso /></label><input type="date" value={ne.dateMiseEnService} onChange={(e) => setNe({ ...ne, dateMiseEnService: e.target.value })} style={inputStyle} /></div>
            {/* ⚠️ UN LOGICIEL N'EST DANS AUCUNE PIÈCE (05/10/2026). Le moteur ne
                lui demande pas de salle, et l'écran ne la propose donc pas :
                exiger une pièce ferait en choisir une au hasard pour pouvoir
                enregistrer — c'est-à-dire fabriquer une donnée fausse pour
                satisfaire un contrôle de complétude. Sa localisation à lui est
                « Où il tourne », juste en dessous, et elle est exigée de la
                même façon. */}
            <div style={{ display: estLogicielNeuf ? 'none' : undefined }}>
              <label style={lbl} htmlFor="eq-piece">Salle * <MarqueIso /></label>
              <Selecteur id="eq-piece" value={ne.emplacementCode} onChange={(v) => setNe({ ...ne, emplacementCode: v })} style={inputStyle}>
                <option value="">— choisir —</option>
                {pieces.map((x) => <option key={x.code} value={x.code}>{x.libelle}</option>)}
              </Selecteur>
              {/* ⚠️ Aucune échappatoire « autre… ». Un champ libre en secours se
                  remplit dès la première pièce oubliée, et l'on retrouve les 54
                  graphies en six mois. La bonne réponse à une salle manquante
                  est de l'AJOUTER au plan du laboratoire, où elle servira à tout
                  le monde — pas de la retaper dans une fiche. */}
              {!pieces.length && (
                <div style={{ fontSize: 12.5, color: 'var(--amber-600)', marginTop: 3 }}>
                  Aucune pièce au plan du laboratoire : ajoutez-les d’abord (Paramétrage → Plan du laboratoire).
                </div>
              )}
            </div>
            {/* OÙ LE LOGICIEL TOURNE — la « localisation » d'un logiciel, et
                une rubrique exigée §6.4.2 à ce titre. « Un logiciel peut être
                relié à un ordinateur ou à tous nos ordinateurs s'il est
                accessible en ligne » (RQ, 05/10/2026).
                ⚠️ « Tous » est un DRAPEAU, pas une liste de 33 codes : cocher
                les postes un à un donnerait une liste fausse, et fausse en
                silence, le jour où le parc en gagne un. */}
            {estLogicielNeuf && (
              <div style={{ gridColumn: '1 / -1', display: 'grid', gap: 7 }}>
                <span style={lbl}>Où il tourne <MarqueIso /></span>
                <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 12.5, cursor: 'pointer' }}>
                  <input type="checkbox" checked={ne.accesEnLigne}
                    onChange={(e) => setNe({ ...ne, accesEnLigne: e.target.checked, ordinateursCodes: e.target.checked ? [] : ne.ordinateursCodes })} />
                  <span><strong>Accessible en ligne</strong> — donc depuis <strong>tous</strong> les
                    ordinateurs du laboratoire, sans être installé sur aucun en particulier.</span>
                </label>
                {!ne.accesEnLigne && (
                  <SelecteurPastilles mono modifiable unite="ordinateur"
                    ariaLabel="Ordinateurs sur lesquels ce logiciel est installé"
                    placeholder="Choisir un ordinateur…"
                    vide="Désignez les ordinateurs, ou cochez « accessible en ligne »."
                    options={ordinateursDuParc}
                    choisis={ne.ordinateursCodes}
                    onChange={(v) => setNe({ ...ne, ordinateursCodes: v })} />
                )}
              </div>
            )}
            {estLogicielNeuf && (
              <div style={{ gridColumn: '1 / -1', display: 'grid', gap: 7 }}>
                <span style={lbl}>Appareils pilotés</span>
                <SelecteurPastilles mono modifiable unite="appareil"
                  ariaLabel="Appareils que ce logiciel pilote"
                  placeholder="Choisir un appareil…"
                  vide="Aucun : un logiciel autonome (convertisseur, aide à l’interprétation) n’en pilote pas."
                  options={pilotablesDuParc}
                  choisis={ne.equipementsPilotes}
                  onChange={(v) => setNe({ ...ne, equipementsPilotes: v })} />
              </div>
            )}
            <div><label style={lbl}>Discipline</label><Selecteur value={ne.discipline} onChange={(v) => setNe({ ...ne, discipline: v })} style={inputStyle}><option value="">—</option>{discs.map((d) => <option key={d.name} value={d.name}>{d.name}</option>)}</Selecteur></div>
            <div><label style={lbl}>Périodicité (mois)</label><input type="number" value={ne.periodiciteMois} onChange={(e) => setNe({ ...ne, periodiciteMois: +e.target.value || 12 })} style={inputStyle} /></div>
          </div>
          {/* Les HABILITATIONS de l'appareil, au moment de l'ajout (28/10/2026) :
              elles décident des postes où il sert — on ne choisit plus de postes. */}
          <ChoixHabilitations habs={habsRef} proposees={habProposees} nature={ne.nature} choix={habChoisies} onChoix={setHabChoisies} />
          {/* ⚠️ CE TEXTE ANNONÇAIT L'INCOMPLÉTUDE COMME UNE FATALITÉ — « la fiche
              restera marquée incomplète […] complétez-la ensuite ». C'était
              vrai, et c'était le défaut : le formulaire ne demandait pas ce
              qu'il exigeait. Il dit maintenant où l'on en est. */}
          <ResteAFaire ne={ne} />
          </div>
        </Modale>
      )}
      <div className="ged-2col-wide ged-colonnes-defilantes" style={{ display: 'grid', gridTemplateColumns: '380px minmax(0, 1fr)', gap: 18, alignItems: 'start' }}>
        <div>
          <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', marginBottom: 12 }} role="group" aria-label="Famille d’équipement">
            {([
              ['toutes', 'Tout le parc'],
              ['analytique', 'Automates'],
              ['intermediaire', 'Équipements intermédiaires'],
              ['informatique', 'Informatique'],
              ['logiciel', 'Logiciels'],
              ['autre', 'Autres'],
            ] as const).map(([k, label]) => {
              /* ⚠️ LE MÊME TEST QUE LE FILTRE, et non plus sa recopie. Les deux
                 étaient écrits deux fois, à vingt lignes d'écart : au 05/10/2026
                 ils avaient DÉJÀ divergé — la famille « Logiciels » ajoutée au
                 filtre ne l'était pas au compteur, qui aurait annoncé le nombre
                 d'« Autres » sous l'étiquette « Logiciels ». Un compteur qui
                 compte autre chose que ce qu'on voit est pire qu'un compteur
                 absent (c'est le relevé MAJ-14, déjà payé une fois ici). */
              const n = equips.filter((e) => estDeLaFamille(e.categorie, k)).length;
              if (!n && k !== 'toutes') return null;
              const on = famille === k;
              return (
                <button key={k} onClick={() => setFamille(k)} className="ged-btn" aria-pressed={on}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: 'var(--font-sans)',
                    fontSize: 12.5, fontWeight: 700, padding: '6px 12px', borderRadius: 'var(--radius-pill)',
                    cursor: 'pointer',
                    background: on ? 'var(--navy-900)' : 'transparent',
                    color: on ? 'var(--surface-card)' : 'var(--text-body)',
                    border: `1px solid ${on ? 'var(--navy-900)' : 'var(--border-default)'}`,
                  }}>
                  {label}<span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, opacity: 0.75 }}>{n}</span>
                </button>
              );
            })}
          </div>
          {/* ── L'INTERRUPTEUR DE RECETTE (BLOQ-04) ──────────────────────────
              Il n'apparaît QUE sur une instance de recette : `session.recette`
              est rendu par le moteur, qui refuse la bascule en production. Un
              interrupteur affiché en production, même sans effet, ferait croire
              qu'il existe des objets cachés dans le parc réel — c'est
              exactement l'inquiétude qu'on veut éviter sur un registre
              présenté en audit. */}
          {session.recette?.ouvert && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: 'var(--text-muted)', marginBottom: 10 }}>
              <input type="checkbox" checked={inclureRecette} onChange={(e) => setInclureRecette(e.target.checked)} />
              Inclure les objets de recette
              <span style={{ fontSize: 12.5 }}>— du décor de démonstration, écarté de tous les compteurs et registres.</span>
            </label>
          )}
          <FilterBar
            q={q} onQ={setQ} placeholder="Rechercher un équipement…"
            chips={[
              { k: 'tous', label: 'Tous', n: duParc.length },
              { k: 'echus', label: 'Échéance dépassée', n: echus.length },
              { k: 'incompletes', label: 'Fiche incomplète', n: incompletes.length },
              { k: 'prets', label: 'Mis à disposition', n: prets.length },
              { k: 'nonraccordes', label: 'Non raccordé', n: nonRaccordes.length },
              { k: 'metrologie', label: 'Équipements de métrologie', n: deMetrologie.length },
              { k: 'silsansreponse', label: 'SIL : sans réponse', n: silSansReponse.length },
              { k: 'silnon', label: 'Non relié au SIL', n: silNonRelies.length },
              { k: 'regulariser', label: 'À régulariser', n: aRegulariser.length },
              { k: 'service', label: 'En service', n: enService.length },
              { k: 'arret', label: 'Maintenance / arrêt', n: arretes.length },
            ]}
            actif={filtre} onChip={setFiltre}
            total={equips.filter(dansFamille).length} affiches={equipsFiltres.length}
            onReset={() => { setQ(''); setFiltre('tous'); setFamille('analytique'); }} />
          <div style={{ ...card, overflow: 'hidden' }}>
            <div className="ged-table-head" role="group" aria-label="Trier la liste du parc"
              style={{ display: 'flex', gap: 14, padding: '9px 14px', background: 'var(--gray-25)', borderBottom: '1px solid var(--border-default)' }}>
              <BoutonsTri label="Trier la liste du parc" tri={tri} onTri={onTri}
                options={[{ cle: 'code', libelle: 'Code' }, { cle: 'nom', libelle: 'Nom' }, { cle: 'echeance', libelle: 'Échéance' }]} />
            </div>
            {equipsFiltres.map((e) => (
              <button key={e.documentId} onClick={() => select('selectedEquip', e.documentId)} className="ged-row"
                style={{ display: 'block', width: '100%', textAlign: 'start', background: selectedEquip === e.documentId ? 'var(--cyan-50)' : 'none', border: 'none', borderTop: '1px solid var(--border-subtle)', padding: '12px 14px', cursor: 'pointer', fontFamily: 'var(--font-sans)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  {e.code && <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, fontWeight: 700, color: 'var(--text-muted)' }}>{e.code}</span>}
                  <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-strong)' }}>{e.nom}</span>
                  {pill(vocabMeta(EQUIP_STATES, etatEquip(e.statut, e.miseEnService).cle))}
                  {e.estPret && <BadgePret e={e} />}
                  {exigeMetrologie(e.categorie) && e.statut === 'en_service'
                    && (!e.metrologie?.raccordement || e.metrologie.raccordement === 'aucun') && (
                    <span title="Équipement intermédiaire dont le raccordement métrologique n’est pas établi (ISO 15189 §6.5)"
                      style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--red-600)', cursor: 'help' }}>non raccordé</span>
                  )}
                  {e.ficheComplete === false && (
                    <span title="Fiche d’identification incomplète (ISO 15189 §6.4.2)"
                      style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--amber-600)', cursor: 'help' }}>fiche incomplète</span>
                  )}
                  {/* La nature ne se devine pas d'un coup d'œil sur « ACC26 » :
                      c'est pourtant elle qui décide si l'appareil peut porter
                      une carte de contrôle. */}
                  {/* ⚠️ LES QUATRE NATURES, et non deux énumérées (05/10/2026).
                      `logiciel` existait depuis le 12/09 et n'avait aucune
                      pastille : à la liste, une fiche logiciel se présentait
                      exactement comme un équipement de mesure, alors que c'est
                      cette nature qui décide si l'appareil peut porter une
                      carte de contrôle. La nature est LUE de la fiche plutôt
                      qu'énumérée ici : une cinquième ne pourra plus être
                      muette. `mesure` reste sans pastille — c'est le cas
                      général, et le marquer bruiterait 46 lignes sur 248. */}
                  {natureEquip(e.nature).key !== 'mesure' && (
                    <span title={natureEquip(e.nature).aide}
                      style={{
                        fontSize: 12.5,
                        fontWeight: 700,
                        color: natureEquip(e.nature).key === 'metrologie' ? 'var(--cyan-700)' : 'var(--text-muted)',
                        cursor: 'help',
                      }}>{natureEquip(e.nature).court}</span>
                  )}
                  {e.codeProvisoire === true && (
                    <span title="Code d’inventaire fabriqué par l’outil, ou désignation à préciser : allez lire l’étiquette de l’appareil, puis régularisez la fiche dans l’onglet « Identification »."
                      style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--amber-600)', cursor: 'help' }}>à régulariser</span>
                  )}
                </div>
                <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 3 }}>
                  {e.localisation || 'localisation inconnue'} · échéance{' '}
                  <span style={{ fontFamily: 'var(--font-mono)', fontWeight: overdue(echeance(e)) ? 700 : 400, color: overdue(echeance(e)) ? 'var(--red-600)' : undefined }}>
                    {echeance(e) ? fmtDate(echeance(e) as string) : 'indéterminée'}
                  </span>
                </div>
              </button>
            ))}
            {attend && <Skeleton rows={4} height={54} />}
            {!attend && equipsFiltres.length === 0 && (
              <EmptyState
                titre={equips.length === 0 ? 'Aucun équipement au parc.' : 'Aucun équipement ne correspond à ces critères.'}
                indice={equips.length === 0 ? 'Ajoutez vos automates et enceintes pour suivre leurs échéances de métrologie.' : 'Élargissez la recherche ou revenez à « Tous ».'}
                action={equips.length > 0
                  ? <Bouton taille="sm" onClick={() => { setQ(''); setFiltre('tous'); }}>Réinitialiser les filtres</Bouton>
                  : undefined} />
            )}
          </div>
        </div>
        {sel
          ? <DossierEquipement e={sel} ouvrirSurMes={accueil === sel.documentId} />
          : <div style={{ ...card, padding: '40px 24px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13.5 }}>Sélectionnez un équipement.</div>}
      </div>
    </div>
  );
}

/**
 * Marque d'un appareil mis à disposition.
 *
 * Rouge tant que son aptitude n'a pas été vérifiée : dans cet état il ne devrait
 * pas rendre de résultats, et c'est la seule chose qu'on veut voir sans ouvrir
 * la fiche. Neutre une fois restitué — il n'est plus au laboratoire.
 */
/* L9 : sur la Pastille du socle (É-12) — la logique (restitué / à risque / prêt) reste ici. */
function BadgePret({ e }: { e: GedEquipement }) {
  const restitue = !!e.pret?.dateRestitution;
  const aRisque = !restitue && !e.pret?.verifieAvantUsage && e.statut === 'en_service';
  return (
    <Pastille ton={aRisque ? 'danger' : 'muted'}
      title={restitue
        ? `Appareil de prêt restitué le ${e.pret?.dateRestitution}`
        : aRisque
          ? 'Appareil de prêt EN SERVICE dont l’aptitude n’a pas été vérifiée (ISO 15189 §6.4.3)'
          : `Appareil mis à disposition par ${e.pret?.proprietaire || 'un tiers'}`}>
      {restitue ? 'prêt restitué' : aRisque ? 'prêt — aptitude non vérifiée' : 'prêt'}
    </Pastille>
  );
}

/* --------------------------- dossier d'un équipement --------------------- */

type OngletEquip = 'identification' | 'mes' | 'vie' | 'maintenance' | 'calibrations' | 'examens' | 'logiciels' | 'pieces' | 'liens';

/* « QUI LE MAÎTRISE » RETIRÉ (Ines, 29/10/2026) : « on a déjà les personnes
   habilitées dessus, ça me suffit ». Le bloc « Habilitations » de la fiche dit
   qui est habilité sur l'appareil, et à quel niveau. */

/**
 * LA MARQUE « EXIGÉ PAR LA 15189 » — demandée nommément (RQ, 11/09/2026).
 *
 * « Je voudrais un popup avec tous les onglets à remplir (signaler ceux qui
 *  sont demandés par la 15189). »
 *
 * ⚠️ ELLE SE DISTINGUE DE L'ASTÉRISQUE, et il fallait choisir. L'astérisque dit
 * « le formulaire refusera sans » — c'est une contrainte d'OUTIL. Celle-ci dit
 * « un auditeur le demandera » — c'est une exigence de NORME, et les deux ne se
 * lèvent pas de la même façon : on peut créer la fiche sans, et la compléter
 * le lendemain quand le bon de livraison arrive. Les confondre obligerait à
 * mentir sur l'une ou l'autre.
 */
function MarqueIso() {
  return (
    <span title="Rubrique d’identification exigée par l’ISO 15189 §6.4.2 — la fiche reste marquée incomplète tant qu’elle manque."
      style={{
        fontSize: 9.5, fontWeight: 700, letterSpacing: '.04em', cursor: 'help',
        color: 'var(--amber-600)', border: '1px solid var(--amber-500)',
        borderRadius: 'var(--radius-sm)', padding: '0 4px', marginInlineStart: 4,
        verticalAlign: 'middle',
      }}>15189</span>
  );
}

/**
 * OÙ L'ON EN EST DES SIX RUBRIQUES — dit pendant la saisie, pas après.
 *
 * ⚠️ IL NE BLOQUE RIEN, et c'est délibéré. Une fiche peut légitimement naître
 * incomplète : l'appareil est là, le bon de livraison arrive la semaine
 * suivante. Refuser la création obligerait à inventer un fournisseur pour
 * pouvoir enregistrer — c'est-à-dire à fabriquer une donnée fausse pour
 * satisfaire un contrôle de complétude. On DIT, on ne barre pas.
 */
/**
 * LA FAMILLE D'APPAREIL, en un seul endroit.
 *
 * ⚠️ UN SEUL TEST pour le filtre de la liste ET pour les compteurs des pastilles.
 * Les deux étaient écrits deux fois, à vingt lignes d'écart, et ils avaient déjà
 * divergé : le relevé MAJ-14 (26/08/2026) portait exactement sur deux barres de
 * filtres qui annonçaient des totaux inconciliables. « Un compteur qui compte
 * autre chose que ce qu'on voit est pire qu'un compteur absent. »
 *
 * ⚠️ « AUTRES » EST UN REPLI, et c'est ce qui rend ce module nécessaire : la
 * branche est « ni automate, ni informatique, ni logiciel, ni intermédiaire »,
 * si bien qu'une famille ajoutée à `EQUIP_CATEGORIES` sans passer ici y tombe
 * toute seule, en silence. Les logiciels s'y seraient rangés avec le mobilier.
 */
export function estDeLaFamille(categorie: string | null | undefined, famille: string): boolean {
  const c = String(categorie || '').trim();
  if (famille === 'toutes') return true;
  if (famille === 'analytique') return c === 'automate';
  if (famille === 'informatique') return c === 'informatique';
  if (famille === CATEGORIE_LOGICIEL) return c === CATEGORIE_LOGICIEL;
  if (famille === 'intermediaire') return exigeMetrologie(c);
  return !['automate', 'informatique', CATEGORIE_LOGICIEL].includes(c) && !exigeMetrologie(c);
}

function ResteAFaire({ ne }: { ne: Record<string, unknown> }) {
  /* ⚠️ L'ÉTAT DU FORMULAIRE EST À PLAT, LA FICHE NE L'EST PAS. La liste exigée
     d'un logiciel porte `logiciel.version` ; le formulaire, lui, tient
     `logicielVersion` — un état imbriqué se met à jour par recopie, et c'est
     ainsi qu'on perd une clé sans s'en apercevoir. On recompose donc la FORME
     de la fiche avant d'interroger la liste, exactement comme à l'envoi.
     Sans cela, la version serait annoncée manquante même saisie : un drapeau
     qu'on ne peut pas lever est pire qu'un drapeau absent. */
  const fiche = {
    ...ne,
    logiciel: { version: ne.logicielVersion, releveLe: ne.logicielReleveLe },
    /* `localisation` est DÉRIVÉE de la salle choisie au plan du laboratoire —
       le texte libre avait produit 54 graphies pour 19 pièces. La liste exigée
       la nomme, l'écran la déduit. */
    localisation: ne.emplacementCode,
  };
  const manque = rubriquesManquantes(fiche);
  return (
    <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.55 }}>
      Un code d’inventaire est attribué automatiquement.{' '}
      {manque.length === 0 ? (
        <strong style={{ color: 'var(--green-600)' }}>
          Les six rubriques du §6.4.2 sont renseignées : la fiche naîtra complète.
        </strong>
      ) : (
        <>
          <strong style={{ color: 'var(--amber-600)' }}>
            {manque.length} rubrique(s) du §6.4.2 encore vide(s)
          </strong>
          {' : '}{manque.map((r) => r.label).join(', ')}. La fiche sera marquée incomplète —
          ce n’est pas bloquant, et cela se complète dans l’onglet « Identification ».
        </>
      )}
    </p>
  );
}

/**
 * LES LOGICIELS QUI PILOTENT CET APPAREIL.
 *
 * ── La demande (RQ, 11/09/2026) ────────────────────────────────────────────
 *
 * « Suivi des logiciels comme équipement. Rajouter par exemple le SCA et
 *  Boxylab. Les logiciels qui pilotent les machines doivent être reliés à ces
 *  machines — par exemple le SCA devra être relié au microscope. Créer un
 *  nouvel onglet pour chaque équipement avec le logiciel (possibilité d'avoir
 *  plusieurs logiciels). Il y a aussi les logiciels stand alone qui ne sont pas
 *  forcément reliés à un équipement, comme un convertisseur ou un outil d'aide
 *  à l'interprétation. »
 *
 * ⚠️ UN LOGICIEL EST UNE FICHE D'ÉQUIPEMENT, de nature `logiciel`. Il a donc
 * déjà, sans qu'on écrive une ligne : un code d'inventaire, un fournisseur (son
 * éditeur), un contrat de maintenance, une date de mise en service, une fiche de
 * vie, des pièces jointes et un journal chaîné. Lui refaire un registre à part
 * aurait été recopier vingt écrans pour changer un mot.
 *
 * ⚠️ CE QU'IL N'A PAS, et c'est garanti par le moteur : ni carte de contrôle, ni
 * calibration, ni raccordement métrologique. Un logiciel ne dérive pas, il
 * change de VERSION — et c'est la version, avec la date de son relevé, qui est
 * la rubrique d'identification à tenir.
 *
 * ⚠️ RIEN NE S'ÉCRIT DEPUIS CET ONGLET, et ce n'est pas un oubli. Le lien est
 * porté par le LOGICIEL (`equipementsPilotes`), qui nomme les appareils qu'il
 * commande. L'ouvrir aussi d'ici donnerait deux endroits pour écrire la même
 * chose, et la question « lequel fait foi » se poserait au premier désaccord.
 * On lit ici, on écrit là-bas.
 */
function LogicielsDeLAppareil({ code, logiciels }: { code: string; logiciels: GedEquipement[] }) {
  const { select } = useGed();
  if (!logiciels.length) {
    return (
      <EmptyState
        titre="Aucun logiciel déclaré sur cet appareil"
        indice={'Ce n’est pas forcément un manque : beaucoup d’appareils n’en portent aucun. '
          + 'Le rattachement se déclare sur la fiche DU LOGICIEL — dans « Identification », '
          + 'à la rubrique « Appareils pilotés ». C’est lui qui nomme les machines qu’il commande.'} />
    );
  }
  return (
    <div style={{ display: 'grid', gap: 10 }}>
      <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.55 }}>
        {logiciels.length} logiciel(s) déclarent piloter <strong style={{ fontFamily: 'var(--font-mono)' }}>{code}</strong>.
        Le rattachement se règle sur leur fiche, pas ici.
      </p>
      <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
        {logiciels.map((l, i) => {
          const v = (l as unknown as { logiciel?: { version?: string; edition?: string; releveLe?: string } }).logiciel;
          return (
            <div key={l.documentId} style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', padding: '11px 14px', borderTop: i ? '1px solid var(--border-subtle)' : 'none' }}>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, fontWeight: 700, color: 'var(--color-primary-active)' }}>{l.code}</span>
              <span style={{ flex: '1 1 180px', fontSize: 13.5, fontWeight: 600, color: 'var(--text-strong)' }}>{l.nom}</span>
              {/* ⚠️ LA VERSION SANS SA DATE NE VAUT RIEN : au bout d'un an, on ne
                  sait plus si « 6.7.39 » décrit l'appareil d'aujourd'hui ou
                  celui de la reprise. On affiche donc les deux, ou on dit que
                  le relevé manque. */}
              {v?.version ? (
                <span style={{ fontSize: 12.5, color: 'var(--text-body)' }}>
                  version <strong style={{ fontFamily: 'var(--font-mono)' }}>{v.version}</strong>
                  {v.edition ? ` · ${v.edition}` : ''}
                  {v.releveLe
                    ? <span style={{ color: 'var(--text-muted)' }}> · relevée le {fmtDate(v.releveLe)}</span>
                    : <span style={{ color: 'var(--amber-600)' }}> · relevé non daté</span>}
                </span>
              ) : (
                <span style={{ fontSize: 12.5, color: 'var(--amber-600)' }}>version non relevée</span>
              )}
              <Bouton taille="sm" onClick={() => select('selectedEquip', l.documentId)}>Ouvrir la fiche</Bouton>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * CE QU'UNE NATURE NE PORTE PAS — dit une fois, pour les deux onglets.
 *
 * ⚠️ LE DÉFAUT CORRIGÉ (05/10/2026). L'onglet « Contrôles internes » nommait
 * `accessoire` et `metrologie` et retombait, pour TOUTE AUTRE nature, sur le
 * tableau de saisie des cartes. `logiciel` — ajoutée le 12/09 — y tombait donc :
 * l'écran proposait de créer une carte de contrôle que le moteur refuse
 * (`refusResultat`, lifecycle de `ged-cqi-carte`). L'onglet « Calibrations »
 * était pire : il n'avait AUCUNE garde, et proposait la saisie aux QUATRE
 * natures alors que le contrôleur n'en accepte qu'une.
 *
 * « Une liste où 85 % des entrées mènent à un refus n'est pas une liste, c'est
 * un piège » — c'est le constat A8, déjà payé une fois sur le sélecteur du
 * contrôle interne. Un écran qui propose ce que le moteur refuse ne se découvre
 * qu'au moment où l'on a saisi.
 *
 * ⚠️ ÉCRIT EN LISTE BLANCHE : `porteResultat` répond « oui » pour `mesure` et
 * rien d'autre. Une cinquième nature sera donc fermée par défaut, et ouverte
 * sciemment — l'inverse de ce qui vient d'arriver à `logiciel`.
 */
function NePorteRien({ e, quoi }: { e: GedEquipement; quoi: 'cartes' | 'calibrations' }) {
  const nat = natureEquip(e.nature);
  /* Le message renvoie vers CE QUI EST OUVERT à cette nature — et ce n'est pas
     le même endroit selon la nature. « Rattachement impossible » laisse sans
     issue ; la bonne manœuvre est toujours ailleurs, et elle existe. */
  const ailleurs = nat.key === 'accessoire'
    ? <>Le contrôle interne et les calibrations se lisent sur l’équipement de mesure que cet accessoire sert.</>
    : nat.key === 'metrologie'
      ? (
        <>Ce qui se surveille sur cet appareil est la grandeur qu’il tient ou délivre — la température
          d’une enceinte, le volume d’une micropipette, la vitesse d’une centrifugeuse. Cela se raccorde
          et se vérifie dans l’onglet <strong>Métrologie</strong> de l’identification.</>
      )
      : (
        <>Ce qui se suit sur un logiciel, c’est sa <strong>version</strong> et la date de son relevé —
          onglet <strong>Identification</strong>. Les contrôles internes se lisent sur les appareils
          qu’il pilote, pas sur lui.</>
      );
  return (
    <div style={{ display: 'grid', gap: 8 }}>
      <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.6 }}>
        <strong style={{ color: 'var(--text-strong)' }}>{nat.titre}</strong> — {nat.aide}
      </p>
      <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.6 }}>
        {quoi === 'cartes'
          ? 'Aucune carte de contrôle interne ne peut donc lui être rattachée : une carte suit la justesse d’un résultat, et il n’y en a pas ici. '
          : 'Aucune calibration ne peut donc lui être rattachée : on ne calibre que ce qui rend un résultat. '}
        {ailleurs}
      </p>
    </div>
  );
}

function DossierEquipement({ e, ouvrirSurMes }: { e: GedEquipement; ouvrirSurMes?: boolean }) {
  const { smq, session, liens, events, loadJournal, maintenances, equips, ncs } = useGed();
  const [ongletChoisi, setOnglet] = useState<OngletEquip>(ouvrirSurMes ? 'mes' : 'identification');
  /* Calibrations et Examens n'ont d'objet que sur ce qui REND UN RÉSULTAT
     (31/10/2026) : sur un accessoire ou un instrument de métrologie, ils ne
     menaient qu'à un « sans objet ». Ils disparaissent ; un onglet masqué qui
     serait encore choisi retombe sur l'Identification. */
  const mesure = porteResultat(e);
  const onglet: OngletEquip = !mesure && (ongletChoisi === 'calibrations' || ongletChoisi === 'examens') ? 'identification' : ongletChoisi;
  // Une fiche déjà affichée (la précédente) garde son état : on l'amène à l'onglet quand c'est la neuve qui arrive.
  useEffect(() => { if (ouvrirSurMes) setOnglet('mes'); }, [ouvrirSurMes, e.documentId]);
  const [etiquette, setEtiquette] = useState(false);
  const [sortie, setSortie] = useState(false);
  const peutParc = peutGererParc(session.roles);
  const code = e.code || '';
  // Lot EQP-1 — l'échéance affichée ici est la DÉRIVÉE, comme dans la liste et
  // au tableau de bord. Elle porte son motif : « indéterminée » sans explication
  // se lit comme un défaut de l'outil, alors que c'est un constat sur la fiche.
  const ligneParc = (maintenances?.parc || []).find((p) => p.code === code) || null;
  const ech = ligneParc?.echeance || null;
  const echDate = ech?.date || ligneParc?.echeancePosee || null;
  // Comparaison en JOURS, jamais en instants : une échéance due AUJOURD'HUI
  // n'est pas dépassée.
  const overdue = !!echDate && jourIso(echDate) < aujourdhuiIso();

  // Le journal d'un équipement est chaîné sur son CODE, comme celui d'un
  // document l'est sur le sien.
  useEffect(() => {
    if (code) void loadJournal(code);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [e.documentId]);
  const journal = events[code] || [];
  // Les liens se lisent dans les DEUX sens : on compte aussi ceux qui pointent
  // vers cet équipement (cf. composant Liens).
  const nbLiens = liens.filter((l) => (l.sourceType === 'equipement' && l.sourceRef === code) || (l.cibleType === 'equipement' && l.cibleRef === code)).length;
  /* Le compteur de la FICHE DE VIE = ses NC encore ouvertes (31/10/2026) : c'est
     ce qu'on vient y chercher. `e.pannes` était un champ hérité, toujours vide. */
  const nbVie = ncsDeLEquipement(ncs, liens, code).filter((n) => ['declaree', 'encours', 'soldee'].includes(String(n.statut))).length;
  /* Documents : les fichiers versés ET les documents internes liés. */
  const nbDocs = (e.pieces || []).length + liens.filter((l) => (l.sourceType === 'equipement' && l.sourceRef === code && l.cibleType === 'document')
    || (l.cibleType === 'equipement' && l.cibleRef === code && l.sourceType === 'document')).length;
  /* ⚠️ LE LIEN SE LIT À L'ENVERS, et c'est voulu : c'est le LOGICIEL qui nomme
     les appareils qu'il pilote (`equipementsPilotes`), jamais l'inverse. Un
     logiciel en commande plusieurs ; poser le lien des deux côtés créerait deux
     vérités à tenir d'accord, et elles divergeraient au premier renommage.
     La recherche se fait sur le parc DÉJÀ en mémoire — aucun appel de plus. */
  const logicielsDe = React.useMemo(
    () => (equips || []).filter((x) => x.nature === 'logiciel'
      && ((x as unknown as { equipementsPilotes?: string[] }).equipementsPilotes || []).includes(code)),
    [equips, code],
  );
  // Bloquantes dues : ce qui reste à voir pour que le dossier « pousse » vers
  // l'onglet, comme les échéances dépassées poussent vers Fiche de vie.
  const avMES = e.miseEnService ? avancement(e.miseEnService) : null;

  return (
    <div style={{ ...card, padding: 20, display: 'grid', gap: 14 }}>
      {/* En-tête HORS onglets : les repères dont on a besoin quelle que soit la
          section consultée — les cacher ferait perdre le fil. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        {code && <span style={{ fontFamily: 'var(--font-mono)', fontSize: 13.5, fontWeight: 700, color: 'var(--color-primary-active)' }}>{code}</span>}
        <h2 style={{ margin: 0, fontSize: 16.5, fontWeight: 700, color: 'var(--text-strong)', flex: 1 }}>{e.nom}</h2>
        {e.estPret && <BadgePret e={e} />}
        {pill(vocabMeta(EQUIP_STATES, etatEquip(e.statut, e.miseEnService).cle))}
        <BoutonJournal reference={code} quoi="de cet équipement" evenements={journal} />
      </div>
      <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
        {EQUIP_CATEGORIES.find((c) => c.key === e.categorie)?.label || e.categorie}
        {e.marque && <> · {e.marque}</>}
        {e.numeroSerie && <> · <span style={{ fontFamily: 'var(--font-mono)' }}>{e.numeroSerie}</span></>}
        {e.localisation && <> · {e.localisation}</>}
        {e.discipline && <> · {e.discipline}</>}
      </div>
      <div style={{ display: 'flex', gap: 14, fontSize: 12.5, flexWrap: 'wrap', alignItems: 'center' }}>
        <span>Périodicité : <strong>{e.periodiciteMois ? `${e.periodiciteMois} mois` : 'non définie'}</strong></span>
        <span title={ech?.motif || ''}>Prochaine échéance : <strong style={{ fontFamily: 'var(--font-mono)', color: overdue ? 'var(--red-600)' : 'var(--text-strong)' }}>{echDate ? fmtDate(echDate) : 'indéterminée'}</strong>{ech?.motif ? <em style={{ fontStyle: 'normal', color: 'var(--text-muted)', fontSize: 12.5 }}> — {ech.motif}</em> : null}</span>
        <span style={{ flex: 1 }} />
        <Bouton taille="sm" onClick={() => setEtiquette(true)}>
          <I n="print" s={13} />Étiquette
        </Bouton>
        {/* « En service » et « en maintenance » restent d'un clic : ce sont des
            états courants et réversibles. La SORTIE DU SERVICE, elle, se
            motive — la fiche de vie doit dire pourquoi l'appareil a cessé de
            produire des résultats (§6.4.4, recette A15). */}
        {peutParc && ['en_service', 'maintenance'].filter((s) => s !== e.statut).map((s) => (
          <Bouton taille="sm" key={s} onClick={() => void smq(`ged/equip/${e.documentId}/status`, { statut: s },
            undefined, `Changement d’état de ${e.code || e.nom}`)}>{vocabMeta(EQUIP_STATES, s).label}</Bouton>
        ))}
        {peutParc && e.statut !== 'hors_service' && (
          <Bouton taille="sm" onClick={() => setSortie(true)}>Hors service…</Bouton>
        )}
      </div>

      {sortie && (
        <SortieDuService e={e} onFermer={() => setSortie(false)} />
      )}

      {/* ORDRE (31/10/2026) : ce qu'il EST, comment il est né, ce qui lui est
          arrivé, ce qu'on lui fait, ce qu'il produit, puis ce qui l'accompagne.
          Une seule ligne qui défile : neuf onglets ne tiennent pas dans le
          panneau, et sur deux lignes l'onglet actif se perdait. */}
      <OngletsDossier id="equip" actif={onglet} onChange={setOnglet} uneLigne items={[
        { k: 'identification', label: 'Identification' },
        { k: 'mes', label: 'Mise en service', n: avMES && !e.miseEnService?.autoriseLe ? avMES.bloquantesDues.length : undefined },
        { k: 'vie', label: 'Fiche de vie', n: nbVie || undefined },
        { k: 'maintenance', label: 'Maintenance' },
        ...(mesure ? [{ k: 'calibrations' as const, label: 'Calibrations' }] : []),
        /* Pas de compteur : « 25 cartes » ne renseigne sur rien. Ce qu'on
           vient chercher ici, c'est lesquelles sont en rejet — et ça, ça se
           dérive à la lecture, dans l'onglet. */
        /* « Examens » remplace « Contrôles internes » (Ines, 29/10/2026) : la liste des
           analyses que fait l'appareil, et devant chacune, son CQI. */
        ...(mesure ? [{ k: 'examens' as const, label: 'Examens' }] : []),
        // « Documents » (29/10/2026) ; la clé `pieces` reste : l'adresse de l'onglet est un contrat.
        { k: 'pieces', label: 'Documents', n: nbDocs },
        /* ⚠️ « LOGICIELS » (12/09/2026) — « créer un nouvel onglet pour chaque
           équipement avec le logiciel (possibilité d'avoir plusieurs
           logiciels) » (RQ, 11/09).
           Le compteur EST utile ici, contrairement aux cartes de contrôle : la
           question qu'on se pose devant un automate est « qu'est-ce qui le
           pilote », et « 2 » y répond déjà. Zéro aussi — un appareil sans
           logiciel est un fait, pas un manque.
           ⚠️ Caché sur une fiche de nature `logiciel` : un logiciel ne se pilote
           pas lui-même, et l'onglet n'aurait rien à montrer. Ses appareils à lui
           se déclarent dans l'Identification. */
        ...(e.nature === 'logiciel' ? [] : [{ k: 'logiciels' as const, label: 'Logiciels', n: logicielsDe.length }]),
        { k: 'liens', label: 'Liens & NC', n: nbLiens },
      ]} />

      <PanneauDossier id="equip" actif={onglet}>
        {onglet === 'identification' && <Identification e={e} />}
        {onglet === 'mes' && <MiseEnService e={e} onOnglet={(k) => setOnglet(k as OngletEquip)} />}
        {onglet === 'vie' && <FicheDeVie e={e} onVersMaintenance={() => setOnglet('maintenance')} onVersCalibrations={() => setOnglet('calibrations')} />}
        {/* Plan de maintenance PRÉVENTIVE de cet appareil (§6.4.4, lot N4) —
            l'accès par équipement demandé en N4-6. Distinct de « Fiche de
            vie », qui enregistre APRÈS COUP ce qui est arrivé (pannes,
            interventions) : ici on déclare AVANT ce qui doit arriver, et on
            l'acquitte. Même composant que l'écran dédié, avec l'équipement
            fixé — un second écran finirait par ne plus dire les mêmes dates. */}
        {onglet === 'maintenance' && (code
          ? <Maintenances equipement={code} onVersVie={() => setOnglet('vie')} />
          : <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)' }}>Cet équipement n’a pas de code d’inventaire : aucune maintenance ne peut lui être rattachée.</p>)}
        {/* MÊME composant que l'écran dédié, avec l'équipement fixé : il lit
            les mêmes routes. Un second écran finirait par diverger. */}
        {onglet === 'calibrations' && (!porteResultat(e)
          ? <NePorteRien e={e} quoi="calibrations" />
          : code
            ? <Calibrations equipement={code} />
            : <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)' }}>Cet équipement n’a pas de code d’inventaire : ses calibrations ne peuvent pas lui être rattachées.</p>)}
        {/* CQI-5 — les cartes de contrôle de cet automate. Le lien inverse
            existait déjà sur chaque carte ; il ne se lisait que dans un sens.
            Même route que l'écran CQI, avec l'équipement en paramètre : l'état
            affiché est le MÊME calcul (`etatCarteCqi`), pas une seconde
            dérivation. Un équipement ACCESSOIRE ne produit aucun résultat et ne
            peut donc porter aucune carte — le dire plutôt que d'afficher un
            tableau vide qu'on lirait comme un manquement. */}
        {onglet === 'examens' && (porteResultat(e)
          ? <ExamensEquipement e={e} />
          : <NePorteRien e={e} quoi="cartes" />)}
        {onglet === 'pieces' && <Pieces e={e} />}
        {onglet === 'logiciels' && <LogicielsDeLAppareil code={code} logiciels={logicielsDe} />}
        {onglet === 'liens' && (
          <div style={{ display: 'grid', gap: 16 }}>
            {/* Sans code d'inventaire, l'équipement n'a pas de référence lisible :
                le moteur de liens ne peut pas le désigner. Le dire, plutôt que
                d'afficher un formulaire qui échouerait. */}
            {code
              ? (
                <>
                  <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)' }}>
                    Rattachez ici les non-conformités causées par cet appareil, les actions
                    correctives qui le concernent, sa procédure d’utilisation ou le lot de
                    réactif mis en cause. Un lien se lit dans les deux sens.
                  </p>
                  <NCLiees e={e} />
                  <Liens type="equipement" refCourante={code} />
                </>
              )
              : <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)' }}>Cet équipement n’a pas de code d’inventaire : il ne peut pas encore être rattaché à une autre fiche.</p>}
          </div>
        )}
        {/* PLUS D'ONGLET « JOURNAL » (RQ, 24/08/2026) — le bouton dédié, en tête du
            dossier comme sur tous les registres, ouvre le même journal en
            fenêtre. Deux chemins pour la même chose obligeaient à savoir
            lequel prendre, et à les tenir tous les deux. */}
      </PanneauDossier>

      {etiquette && <Etiquette e={e} onFerme={() => setEtiquette(false)} />}
    </div>
  );
}

